import type {
  LocationPermission,
  SuggesterStation,
  SuggesterStationStatus,
  SuggesterStatus,
} from './types'

export type { SuggesterStation, SuggesterStatus, LocationPermission } from './types'

/**
 * Live-status cache snapshot, keyed by **naptanId** (spec §8). Pushed in
 * by the host whenever the status preloader / 60 s refresh lands a fresh
 * snapshot. Drives the late-arrival reactive variant flip — a row
 * painted Default flips to Closed / Advisory in place when the cache
 * catches up. Mirrors the iOS `cachedStatuses`.
 */
export type CachedStatuses = Readonly<Record<string, SuggesterStationStatus>>

/**
 * Sub-phase of the Disruption Details screen (spec §5). Mirrors the iOS
 * `DisruptionDetailsPresentation.Phase`:
 * - `rich` — the inline-rich status payload is present; render the body.
 * - `loading` — severity known, rich payload not yet landed; shimmer,
 *   capped at {@link DISRUPTION_LOADING_CAP_MS} before dropping to error.
 * - `error` — the supporting fetch failed / the cap elapsed; soft Retry.
 */
export type DisruptionPhase = 'rich' | 'loading' | 'error'

/**
 * Disruption Details presentation state (spec §5). Set when the user
 * taps a Closed / Advisory row's status composite; cleared on Back /
 * dismiss. Mirrors the iOS `DisruptionDetailsPresentation`.
 */
export interface DisruptionPresentation {
  /** Station the Details screen describes (ring + name + line header). */
  readonly station: SuggesterStation
  /** Severity tag — drives the chip colour. */
  readonly severity: SuggesterStationStatus['severity']
  /** Sub-phase — rich / loading / error. */
  readonly phase: DisruptionPhase
  /**
   * The rich payload, when known (phase `rich`). Carries title / message
   * / appearance / dates / lineIds for the Details body. Absent in
   * `loading` / `error`.
   */
  readonly status?: SuggesterStationStatus
}

/**
 * Disruption Details Loading-shimmer cap (spec §5.2) — after this long
 * without a rich payload landing, drop into the Error sub-state. STRICT
 * cross-platform constant (iOS `disruptionLoadingCapSeconds` = 5.0).
 */
export const DISRUPTION_LOADING_CAP_MS = 5000

/**
 * Which modal alert (if any) is currently shown over the Suggester.
 *
 * - `none` — no alert.
 * - `denied` — the app-drawn "Location Access Required" alert (spec §14).
 *
 * There is intentionally NO `permission` value: the system permission
 * *prompt* is the browser's own native dialog (triggered by
 * `navigator.geolocation`), never an in-app component.
 */
export type SuggesterAlert = 'none' | 'denied'

/**
 * Immutable state of an open Suggester session.
 *
 * Mirrors the cross-platform state machine (spec §4) — six states. The
 * reducer is a pure UDF, identical in shape to the host `metroMapStore`
 * reducer (house style — consistency over pulling in Zustand).
 */
export interface SuggesterState {
  /** Raw search field text (verbatim, not normalised). */
  readonly query: string
  /** Results-area lifecycle — see {@link SuggesterStatus}. */
  readonly status: SuggesterStatus
  /** Matches for the settled query (meaningful in `results` / `offline`). */
  readonly results: readonly SuggesterStation[]
  /**
   * `true` while the current results came from the bundled offline
   * index after a backend failure — drives the offline notice.
   */
  readonly offline: boolean
  /** Recently-selected station ids, most-recent first (persisted). */
  readonly recentIds: readonly string[]
  /** Favourited station ids, from the shared FavoritesService (§16). */
  readonly favoriteIds: readonly string[]
  /** Full distance-sorted nearby list — shown in the `nearby` state. */
  readonly nearby: readonly SuggesterStation[]
  /** The 3 closest nearby stations — shown in the `idle` Nearby section. */
  readonly nearbyIdle: readonly SuggesterStation[]
  /** Geolocation permission state — see {@link LocationPermission}. */
  readonly locationPermission: LocationPermission
  /**
   * Whether the "use my location" banner is shown (#38, spec §14).
   *
   * **Reducer-owned, not host-prescribed. TASK-412 W — DEFAULT OFF.** The
   * banner must never flash ON at open. It starts `false`; the mount-time
   * permission probe (`LocationProbed`) turns it ON only for `prompt` /
   * `denied` (the only states where the "use my location" CTA is
   * actionable). On `granted` it stays `false` + the host auto-resolves
   * Nearby so the user lands directly on the Nearby section. `NearbyResolved`
   * also clears it as defence-in-depth — covers the in-session tap path
   * (prompt → grant → resolve) where the mount probe saw `prompt`. Mirrors
   * the iOS `showsLocationBanner` inverted default + `.task` probe.
   */
  readonly showsLocationBanner: boolean
  /**
   * Live-status cache, naptanId-keyed (spec §8 late-arrival reactivity).
   * WHOLESALE REPLACED by `CachedStatusesUpdated` (cross-platform contract,
   * TASK-629 — mirrors iOS/Android: the host always pushes the full,
   * authoritative snapshot, so a resolved disruption — key absent from the
   * new push — clears, and a changed severity for an already-cached key
   * updates in place; a "gaps-only" merge left resolved disruptions stuck
   * forever). A live inline `station.status` (fresher, from an actual
   * search/nearby hit) still wins over this cache at RENDER time — see
   * {@link applyCachedStatuses} — that precedence is unaffected by how the
   * cache itself is populated.
   */
  readonly cachedStatuses: CachedStatuses
  /**
   * Disruption Details presentation (spec §5), or `null` when the screen
   * is not shown. Set by `DisruptionOpened`, cleared by
   * `DisruptionDismissed`. A late `CachedStatusesUpdated` flips a
   * `loading` presentation to `rich` in place.
   */
  readonly disruption: DisruptionPresentation | null
  /** Which alert is shown — see {@link SuggesterAlert}. */
  readonly alert: SuggesterAlert
  /**
   * Monotonic counter bumped whenever a fresh backend search must run —
   * by `QueryChanged` (non-empty) and by `ConnectivityRestored` (spec
   * §15 offline auto-recovery). The component's search effect keys off
   * this so re-running the SAME query after a reconnection still
   * re-fires, without a same-query `QueryChanged` no-op swallowing it.
   */
  readonly searchNonce: number
}

/**
 * Resting state: empty query, idle status, no results / recents /
 * favourites / nearby, permission `prompt`, no alert.
 */
export const initialSuggesterState: SuggesterState = {
  query: '',
  status: 'idle',
  results: [],
  offline: false,
  recentIds: [],
  favoriteIds: [],
  nearby: [],
  nearbyIdle: [],
  locationPermission: 'prompt',
  // TASK-412 W — DEFAULT OFF; the mount probe turns it on for not-granted.
  showsLocationBanner: false,
  cachedStatuses: {},
  disruption: null,
  alert: 'none',
  searchNonce: 0,
}

/**
 * Events the Suggester store accepts.
 */
export type SuggesterEvent =
  | { kind: 'QueryChanged'; query: string }
  | { kind: 'SearchSettled'; stations: readonly SuggesterStation[]; offline: boolean }
  | { kind: 'RecentsLoaded'; recentIds: readonly string[] }
  | { kind: 'FavoritesChanged'; favoriteIds: readonly string[] }
  | { kind: 'LocationPermissionChanged'; permission: LocationPermission }
  /**
   * Mount-time permission probe result (#38, spec §14). Dispatched once
   * by the component's `useLocationPermission` probe. `granted` hides the
   * banner (and the component auto-resolves Nearby); `prompt` / `denied`
   * leave it visible. Records the observed permission too.
   */
  | { kind: 'LocationProbed'; permission: LocationPermission }
  | { kind: 'AlertRequested'; alert: SuggesterAlert }
  | { kind: 'AlertDismissed' }
  | { kind: 'NearbyResolved'; stations: readonly SuggesterStation[] }
  /**
   * Granted-at-MOUNT Nearby resolve (not an explicit banner tap).
   * Populates the idle Nearby section (`nearby` / `nearbyIdle`) but
   * stays in the IDLE status — so Nearby and Popular render TOGETHER
   * (cross-platform decision 2026-06-18: granted + abroad shows the
   * Heathrow airport stations alongside Popular). The full-screen
   * `nearby` status is reserved for an explicit "use my location" banner
   * tap (`NearbyResolved`). Mirrors the iOS `locationResolvedAtMount`.
   */
  | { kind: 'NearbyResolvedAtMount'; stations: readonly SuggesterStation[] }
  /**
   * Connectivity returned while the Suggester was `offline` (spec §15
   * offline auto-recovery — parity with Android `ConnectivityRestored`).
   * Re-runs the current query against the backend: transitions back to
   * `loading` and bumps `searchNonce`. A no-op unless the state is
   * `offline` with a non-empty query.
   */
  | { kind: 'ConnectivityRestored' }
  /**
   * Fresh live-status cache snapshot from the host (spec §8). Merged
   * gaps-only into `cachedStatuses` — existing entries win (a live
   * `/search` status is fresher than the cache). A no-op when every
   * incoming key is already present. Mirrors the iOS `setCachedStatuses`.
   */
  | { kind: 'CachedStatusesUpdated'; statuses: CachedStatuses }
  /**
   * User tapped a Closed / Advisory row's status composite (spec §5) —
   * open Disruption Details. Opens in `rich` when the station carries an
   * inline status (or one is found in the cache), else `loading`.
   */
  | { kind: 'DisruptionOpened'; station: SuggesterStation }
  /** User tapped Back / dismissed the Disruption Details screen. */
  | { kind: 'DisruptionDismissed' }
  /** User tapped Retry in the Error sub-state — re-enter `loading`. */
  | { kind: 'DisruptionRetry' }
  /**
   * The 5 s Loading-shimmer cap elapsed (spec §5.2) without a rich
   * payload landing — drop the matching presentation into `error`.
   * Ignored when Details has been dismissed or moved to another station.
   */
  | { kind: 'DisruptionLoadingExpired'; stationId: string }

/** Number of nearby stations shown in the idle Nearby section (spec §3.4). */
const NEARBY_IDLE_COUNT = 3

/**
 * TASK-412 S — cap for the location-resolved (expanded) Nearby phase list (the
 * list shown after "use my location"). Idle Nearby is 3; the expanded list was
 * previously UNBOUNDED — `resolveNearby` could return up to ~50 stations, all
 * of which populated the `nearby` state, so a far location showed a 10+-row
 * list. Capped at 8 to match the picker's other list caps
 * (`IDLE_VISIBLE_BUDGET` / recents / popular). Mirrors iOS
 * `SuggesterSystem.nearbyExpandedLimit`.
 */
const NEARBY_RESOLVED_MAX = 8

function byDistanceAsc(stations: readonly SuggesterStation[]): readonly SuggesterStation[] {
  return [...stations].sort(
    (a, b) => (a.distanceMeters ?? Infinity) - (b.distanceMeters ?? Infinity),
  )
}

/**
 * Pure reducer for {@link SuggesterState}. Returns the SAME reference
 * when an event is a no-op so `useSyncExternalStore` consumers can bail
 * out cheaply.
 */
export function suggesterReducer(state: SuggesterState, event: SuggesterEvent): SuggesterState {
  switch (event.kind) {
    case 'QueryChanged': {
      const trimmed = event.query.trim()
      if (trimmed.length === 0) {
        if (
          state.query === '' &&
          state.status === 'idle' &&
          state.results.length === 0 &&
          !state.offline
        ) {
          return state
        }
        return { ...state, query: '', status: 'idle', results: [], offline: false }
      }
      return {
        ...state,
        query: event.query,
        status: 'loading',
        searchNonce: state.searchNonce + 1,
      }
    }
    case 'SearchSettled': {
      // Stale-response guard: the query was cleared while the search was
      // in flight — drop the result.
      if (state.query.trim().length === 0) return state
      if (event.stations.length === 0) {
        // Both an online and an offline search yielding nothing land in
        // `empty` (spec §4 / §15).
        return { ...state, status: 'empty', results: [], offline: event.offline }
      }
      return {
        ...state,
        status: event.offline ? 'offline' : 'results',
        results: event.stations,
        offline: event.offline,
      }
    }
    case 'RecentsLoaded': {
      return { ...state, recentIds: event.recentIds }
    }
    case 'FavoritesChanged': {
      return { ...state, favoriteIds: event.favoriteIds }
    }
    case 'LocationPermissionChanged': {
      // A grant observed mid-session (e.g. the user allowed the browser
      // prompt) hides the banner immediately — the in-session resolve
      // path then lands `NearbyResolved`, but the banner must clear the
      // moment permission flips (#38). `prompt` / `denied` keep it.
      const showsLocationBanner = event.permission === 'granted' ? false : state.showsLocationBanner
      if (
        state.locationPermission === event.permission &&
        state.showsLocationBanner === showsLocationBanner
      ) {
        return state
      }
      return { ...state, locationPermission: event.permission, showsLocationBanner }
    }
    case 'LocationProbed': {
      // #38 mount-time probe (spec §14) + TASK-412 W. The banner starts
      // OFF (W — never flashes on at open); this probe is the only thing
      // that turns it ON. `granted` → stays OFF (the user has nothing to
      // grant; the component auto-resolves Nearby). `prompt` / `denied` →
      // turn it ON (the user still has an action) and do NOT raise the
      // denied alert (the alert is only for an explicit banner tap on a
      // denied permission).
      const showsLocationBanner = event.permission !== 'granted'
      if (
        state.locationPermission === event.permission &&
        state.showsLocationBanner === showsLocationBanner
      ) {
        return state
      }
      return { ...state, locationPermission: event.permission, showsLocationBanner }
    }
    case 'AlertRequested': {
      if (state.alert === event.alert) return state
      return { ...state, alert: event.alert }
    }
    case 'AlertDismissed': {
      if (state.alert === 'none') return state
      return { ...state, alert: 'none' }
    }
    case 'ConnectivityRestored': {
      // Auto-recovery (spec §15): only meaningful while `offline` with a
      // live query. Bumps `searchNonce` so the search effect re-runs the
      // SAME query against the backend; the list is not reordered until
      // that next settled result arrives.
      if (state.status !== 'offline' || state.query.trim().length === 0) return state
      return { ...state, status: 'loading', searchNonce: state.searchNonce + 1 }
    }
    case 'CachedStatusesUpdated': {
      // Wholesale replace (cross-platform contract, TASK-629): the incoming
      // snapshot is the host's full, authoritative live-status map, so it
      // REPLACES the cache outright — a resolved disruption (key dropped
      // from the new snapshot) clears, and an escalated / changed severity
      // for an already-cached key updates in place. No-op (same reference)
      // when the incoming snapshot is identical to what's already cached.
      // An inline `station.status` (fresher, from a live search hit) still
      // wins at render time via `applyCachedStatuses` — untouched here.
      if (statusesEqual(state.cachedStatuses, event.statuses)) return state
      // §5.2 — if Disruption Details is open in `loading` and this push
      // lands the matching station's status, flip it to `rich` in place
      // (the late-arrival hydration path, mirrors iOS setCachedStatuses).
      const disruption = flipDisruptionIfMatched(state.disruption, event.statuses)
      return { ...state, cachedStatuses: event.statuses, disruption }
    }
    case 'NearbyResolved': {
      const sorted = byDistanceAsc(event.stations)
      const capped = sorted.slice(0, NEARBY_RESOLVED_MAX)
      return {
        ...state,
        status: 'nearby',
        nearby: capped,
        nearbyIdle: sorted.slice(0, NEARBY_IDLE_COUNT),
        // Defence-in-depth (#38): the in-session tap path (prompt →
        // grant → resolve) flows through here; the mount probe saw
        // `prompt` and left the banner up, so clear it on resolve.
        showsLocationBanner: false,
        alert: 'none',
      }
    }
    case 'NearbyResolvedAtMount': {
      // Granted-at-mount: populate the idle Nearby section but DO NOT
      // switch to the full-screen `nearby` status, so the idle
      // composition renders Nearby (abroad → Heathrow airport) AND
      // Popular together (cross-platform decision 2026-06-18). Mirrors
      // the iOS `locationResolvedAtMount` reducer branch. The banner is
      // cleared too (the user has nothing left to grant). The explicit
      // banner tap (`NearbyResolved`) is the only path to `nearby`.
      const sorted = byDistanceAsc(event.stations)
      const capped = sorted.slice(0, NEARBY_RESOLVED_MAX)
      return {
        ...state,
        status: 'idle',
        nearby: capped,
        nearbyIdle: sorted.slice(0, NEARBY_IDLE_COUNT),
        showsLocationBanner: false,
        alert: 'none',
      }
    }
    case 'DisruptionOpened': {
      // Resolve the status: the station's inline status first (freshest),
      // then the naptanId-keyed cache (spec §5 / §13). Present → open
      // rich; absent → open loading (the cap / a late cache write moves
      // it on). Mirrors the iOS chevron-tap precedence.
      const status =
        event.station.status ??
        (event.station.naptanId !== undefined
          ? state.cachedStatuses[event.station.naptanId]
          : undefined)
      return { ...state, disruption: presentationFor(event.station, status) }
    }
    case 'DisruptionDismissed': {
      if (state.disruption === null) return state
      return { ...state, disruption: null }
    }
    case 'DisruptionRetry': {
      if (state.disruption === null) return state
      // Re-enter loading; a follow-up cache write flips back to rich, or
      // the 5 s cap rolls it into error again.
      return { ...state, disruption: { ...state.disruption, phase: 'loading', status: undefined } }
    }
    case 'DisruptionLoadingExpired': {
      const current = state.disruption
      if (
        current === null ||
        current.station.id !== event.stationId ||
        current.phase !== 'loading'
      ) {
        return state
      }
      return { ...state, disruption: { ...current, phase: 'error' } }
    }
  }
}

/**
 * Build a {@link DisruptionPresentation} for a station + (optional)
 * status (spec §5). With a status → `rich` carrying the payload; without
 * → `loading`, severity defaulting to the softer `advisory` tone so the
 * chip is never alarmingly red while we wait. Mirrors the iOS
 * `DisruptionDetailsPresentation(station:status:)` bridge.
 */
function presentationFor(
  station: SuggesterStation,
  status: SuggesterStationStatus | undefined,
): DisruptionPresentation {
  if (status !== undefined) {
    return { station, severity: status.severity, phase: 'rich', status }
  }
  return { station, severity: 'advisory', phase: 'loading' }
}

/**
 * Structural equality for two `CachedStatuses` maps (TASK-629) — used only
 * to short-circuit `CachedStatusesUpdated` into a no-op when the host pushes
 * a snapshot identical to what's already cached (same key set, same values).
 * Values are small plain DTO-shaped objects (no functions / cycles), so
 * `JSON.stringify` comparison is exact and cheap.
 */
function statusesEqual(a: CachedStatuses, b: CachedStatuses): boolean {
  const aKeys = Object.keys(a)
  const bKeys = Object.keys(b)
  if (aKeys.length !== bKeys.length) return false
  return aKeys.every((key) => JSON.stringify(a[key]) === JSON.stringify(b[key]))
}

/**
 * If a Disruption Details screen is open in `loading` and `cache` now
 * carries the matching station's status, return the flipped-to-`rich`
 * presentation; otherwise return the input unchanged (same reference).
 */
function flipDisruptionIfMatched(
  disruption: DisruptionPresentation | null,
  cache: Record<string, SuggesterStationStatus>,
): DisruptionPresentation | null {
  if (disruption === null || disruption.phase !== 'loading') return disruption
  const naptanId = disruption.station.naptanId
  if (naptanId === undefined) return disruption
  const status = cache[naptanId]
  if (status === undefined) return disruption
  return { ...disruption, severity: status.severity, phase: 'rich', status }
}

/**
 * G-10 disruption-detail analytics — wire names + the web enum values
 * (LOCKED cross-platform contract, catalog §G-10; iOS = canon). Lives here
 * (with the other pure disruption helpers) so the names/enums have one home
 * and {@link disruptionSettleEvent} is unit-testable without the React tree.
 */
export const EVENT_DISRUPTION_DETAIL_LOADED = 'disruption_detail_loaded'
export const EVENT_DISRUPTION_DETAIL_ERROR = 'disruption_detail_error'
/**
 * Web `disruption_detail_loaded.phase`. Web resolves disruption detail from
 * the on-device status cache (inline / naptanId-keyed) — no live disruption
 * fetch (the Suggester backend is bundled-only) — so a content settle is
 * always `cache`. Contract `network` / `empty` are reserved for iOS/Android.
 */
export const WEB_DISRUPTION_LOADED_PHASE = 'cache'
/**
 * Web `disruption_detail_error.reason`. The only terminal error path on web
 * is the 5 s loading cap elapsing → `timeout`. `network` / `not_found` /
 * `unknown` belong to a real status-fetch path web does not have yet.
 */
export const WEB_DISRUPTION_ERROR_REASON = 'timeout'

/**
 * The G-10 analytics event for a Disruption Details TERMINAL settle, or
 * `null` for the non-terminal `loading` placeholder (no emit). Pure — maps a
 * {@link DisruptionPresentation} onto the `(name, params)` the host sink
 * forwards:
 *
 * - `rich` (content settle) → `disruption_detail_loaded { station_id,
 *   severity, phase: cache }` — severity is always present on a content
 *   settle (`closure | advisory`).
 * - `error` (5 s cap → timeout) → `disruption_detail_error { station_id,
 *   severity, reason: timeout }` — `severity` is **INCLUDED** = the open-time
 *   badge severity carried on the presentation. FINAL ruling (iOS confirmed
 *   the open-time severity is reachable + semantically valid at the timeout
 *   site → iOS includes it, commit `e253b1ec4`; Android matches). On the web
 *   cap path it is the presentation's severity — the value present when
 *   Details opened (the resolved status when known, else the `advisory`
 *   placeholder carried from the status-less open).
 * - `loading` → `null` (placeholder, not a terminal settle).
 *
 * Mirrors the iOS `.detailLoaded` / `.detailError` emits.
 */
export function disruptionSettleEvent(
  presentation: DisruptionPresentation,
): { name: string; params: Record<string, string> } | null {
  if (presentation.phase === 'loading') return null
  if (presentation.phase === 'rich') {
    return {
      name: EVENT_DISRUPTION_DETAIL_LOADED,
      params: {
        station_id: presentation.station.id,
        severity: presentation.severity,
        phase: WEB_DISRUPTION_LOADED_PHASE,
      },
    }
  }
  // phase === 'error' — INCLUDE severity (the open-time badge severity carried
  // on the presentation). Final ruling, reconciled with iOS (e253b1ec4) +
  // Android: the open-time severity is reachable + valid at the timeout site.
  return {
    name: EVENT_DISRUPTION_DETAIL_ERROR,
    params: {
      station_id: presentation.station.id,
      severity: presentation.severity,
      reason: WEB_DISRUPTION_ERROR_REASON,
    },
  }
}

/**
 * Project the naptanId-keyed live-status cache (spec §8) onto a station
 * list at render time, filling the `status` of any station that has none
 * but whose `naptanId` is in the cache. An inline `status` (from a live
 * `/search` / `/popular` / `/nearby` payload) is **fresher** than the
 * cache and is left untouched (freshness rule). Returns the SAME array
 * reference when nothing changed so consumers can bail out cheaply.
 *
 * This is the web mirror of the iOS view-layer cache fallback
 * (`SuggesterView._variant(for:)`): a Recents / Favorites / Nearby /
 * Popular row painted from a status-less payload still shows the badge
 * once the `/status` cache lands a disruption for the same naptanId.
 */
export function applyCachedStatuses(
  stations: readonly SuggesterStation[],
  cachedStatuses: CachedStatuses,
): readonly SuggesterStation[] {
  let changed = false
  const projected = stations.map((station) => {
    if (station.status !== undefined || station.naptanId === undefined) return station
    const cached = cachedStatuses[station.naptanId]
    if (cached === undefined) return station
    changed = true
    return { ...station, status: cached }
  })
  return changed ? projected : stations
}

/**
 * Minimal observable store contract — mirrors the host
 * `metroMapStore.Store` so `useSyncExternalStore` plugs in directly.
 */
export interface SuggesterStore {
  getState: () => SuggesterState
  dispatch: (event: SuggesterEvent) => void
  subscribe: (listener: () => void) => () => void
}

/**
 * Build a Suggester store. Notifies subscribers only when the reducer
 * produces a new state reference (no-op events do not re-render).
 */
export function createSuggesterStore(
  initial: SuggesterState = initialSuggesterState,
): SuggesterStore {
  let state = initial
  const listeners = new Set<() => void>()
  return {
    getState: () => state,
    dispatch: (event) => {
      const next = suggesterReducer(state, event)
      if (next !== state) {
        state = next
        for (const l of listeners) l()
      }
    },
    subscribe: (listener) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }
}
